import { Link } from "react-router-dom";
import { CalendarDays, MapPin, UserPlus, Camera, Users, Phone, Mail } from "lucide-react";
import { useOverview } from "@/lib/api";
import { money } from "@/lib/format";
import ScheduleList from "@/components/ScheduleList";
import { priceText } from "@/components/ActivityCounts";

const LOGO = "/images/fanene-legacy-logo.jpg";
const TAPA = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='24'><path d='M0 24 L12 0 L24 24 Z M24 24 L36 0 L48 24 Z' fill='%23B91C1C' fill-opacity='0.9'/></svg>\")";

function Hero({ s }) {
  return (
    <section className="relative overflow-hidden bg-stone-950" data-testid="home-hero">
      <div className="absolute inset-x-0 top-0 h-6 opacity-80" style={{ backgroundImage: TAPA }} />
      <div className="absolute inset-x-0 bottom-0 h-6 opacity-80 rotate-180" style={{ backgroundImage: TAPA }} />
      <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full bg-red-700/25 blur-3xl" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
        <div className="order-1 lg:order-2 flex justify-center rise rise-1">
          <img src={LOGO} alt="Fanene Legacy — Families are Forever" data-testid="hero-logo"
            className="w-64 sm:w-80 lg:w-full max-w-md aspect-[960/882] object-contain rounded-[2rem] bg-white p-3 shadow-[0_20px_60px_rgba(185,28,28,0.35)] ring-4 ring-red-700/60" />
        </div>
        <div className="order-2 lg:order-1 bg-stone-900/70 backdrop-blur-md rounded-3xl p-7 sm:p-10 border border-white/10 text-amber-50 rise">
          <p className="label-cap !text-red-400">Talofa lava, ʻāiga</p>
          <h1 data-testid="reunion-title" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3">{s.title || "Fanene Family Reunion"}</h1>
          {s.subtitle && <p className="font-serif-accent italic text-xl mt-3 text-amber-100">{s.subtitle}</p>}
          <div className="mt-6 space-y-2 text-lg">
            {s.dates_text && <p data-testid="reunion-dates" className="flex items-center gap-3"><CalendarDays className="w-6 h-6 text-amber-300" />{s.dates_text}</p>}
            {s.location && <p data-testid="reunion-location" className="flex items-center gap-3"><MapPin className="w-6 h-6 text-amber-300" />{s.location}</p>}
          </div>
          <div className="mt-8 flex flex-col xl:flex-row gap-3">
            <Link to="/register" data-testid="hero-register-button" className="btn-primary whitespace-nowrap !bg-amber-500 !text-stone-900 hover:!bg-amber-400"><UserPlus className="w-6 h-6" />Register my family</Link>
            <Link to="/my-family" data-testid="hero-edit-button" className="btn-outline whitespace-nowrap !bg-transparent !text-amber-50 !border-amber-100/50 hover:!bg-white/10">Already registered? Edit</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Fees({ s, activities }) {
  const fees = s.fees || [];
  const paid = activities.filter((a) => Number(a.price_adult) || Number(a.price_keiki));
  const row = (key, label, sub, price) => (
    <li key={key} data-testid={`fee-item-${key}`} className="py-4 flex justify-between gap-4">
      <div><p className="text-lg font-semibold">{label}</p>{sub && <p className="text-stone-600">{sub}</p>}</div>
      <p className="font-heading text-xl font-bold text-[#2B5338] text-right">{price}</p>
    </li>
  );
  return (
    <div className="card-warm rise rise-2" data-testid="fees-card">
      <h2 className="text-2xl sm:text-3xl font-bold text-amber-900">Fees</h2>
      {!fees.length && !paid.length && <p className="text-lg text-stone-600 mt-3">Fees will be posted soon.</p>}
      <ul className="mt-4 divide-y divide-amber-900/10">
        {fees.map((f) => row(f.id, f.label, `${f.per === "family" ? "per family" : "per person"}${f.description ? ` · ${f.description}` : ""}`, f.tbd ? "TBD" : Number(f.amount) ? money(f.amount) : "Free"))}
        {paid.map((a) => row(a.id, a.name, null, priceText(a)))}
        {Number(s.shirt_price) > 0 && row("shirts", "Reunion t-shirts", null, `${money(s.shirt_price)} each`)}
      </ul>
      {s.payment_due && <p className="mt-4 text-lg font-semibold">Activity payments are due in full by {s.payment_due}.</p>}
      {s.payment_instructions && <div className="mt-4 rounded-xl bg-[#FDF6ED] p-4 text-lg whitespace-pre-line"><p className="label-cap mb-1">How to pay</p>{s.payment_instructions}</div>}
    </div>
  );
}

export default function Home() {
  const { data } = useOverview();
  const s = data?.settings || {};
  return (
    <div>
      <Hero s={s} />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-8">
          {s.description && (
            <div className="card-warm rise rise-1"><h2 className="text-2xl sm:text-3xl font-bold text-amber-900">About the reunion</h2>
              <p data-testid="reunion-description" className="text-lg leading-relaxed mt-3 whitespace-pre-line">{s.description}</p>
              {s.address && <p className="mt-4 flex gap-2 text-lg text-stone-700"><MapPin className="w-5 h-5 mt-1" />{s.address}</p>}</div>
          )}
          <div className="card-warm rise rise-2">
            <div className="flex items-center justify-between gap-4 mb-6"><h2 className="text-2xl sm:text-3xl font-bold text-amber-900">Schedule</h2>
              <Link to="/schedule" data-testid="home-full-schedule-link" className="btn-outline">See all</Link></div>
            <ScheduleList items={data?.schedule || []} limit={4} />
          </div>
        </div>
        <div className="lg:col-span-2 space-y-8">
          <Fees s={s} activities={data?.activities || []} />
          <div className="grid grid-cols-2 gap-4 rise rise-3">
            <Link to="/photos" data-testid="home-photos-tile" className="card-warm !p-5 flex flex-col gap-2 hover:shadow-md"><Camera className="w-8 h-8 text-[#8C3415]" /><span className="font-heading text-xl font-semibold">Photos</span></Link>
            <Link to="/my-family" data-testid="home-myfamily-tile" className="card-warm !p-5 flex flex-col gap-2 hover:shadow-md"><Users className="w-8 h-8 text-[#2B5338]" /><span className="font-heading text-xl font-semibold">My family</span></Link>
          </div>
          {(s.contact_name || s.contact_email || s.contact_phone) && (
            <div className="card-warm" data-testid="contact-card"><p className="label-cap">Questions? Contact</p>
              {s.contact_name && <p className="text-xl font-semibold mt-2">{s.contact_name}</p>}
              {s.contact_phone && <a href={`tel:${s.contact_phone}`} className="flex items-center gap-2 text-lg mt-2 underline"><Phone className="w-5 h-5" />{s.contact_phone}</a>}
              {s.contact_email && <a href={`mailto:${s.contact_email}`} className="flex items-center gap-2 text-lg mt-2 underline break-all"><Mail className="w-5 h-5" />{s.contact_email}</a>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
